/**
 * useUserDetails Hook - User Profile Data Fetching
 *
 * WHY THIS ARCHITECTURE:
 * Fetches detailed user profile information from Cognito including
 * user attributes (email, phone, name, etc.) and account metadata.
 * Uses React Query for automatic caching and background refetching.
 *
 * BUSINESS RULES:
 * - Requires active authentication session
 * - Returns all Cognito user attributes
 * - Auto-refetches on window focus (fresh data)
 * - 5-minute stale time (balance freshness vs API calls)
 * - Email and phone number may be unverified
 * - MFA status included in attributes
 *
 * CACHING STRATEGY:
 * - Query Key: ["user-details"] - Single source of truth
 * - Stale Time: 5 minutes (reasonable for profile data)
 * - Cache Time: 10 minutes (keep in background)
 * - Refetch on Window Focus: true (fresh data when user returns)
 * - Enabled: Only when user is authenticated
 *
 * USER ATTRIBUTES STRUCTURE:
 * Cognito returns attributes in array format:
 * [
 *   { Name: "email", Value: "user@example.com" },
 *   { Name: "email_verified", Value: "true" },
 *   { Name: "phone_number", Value: "+447700900000" },
 *   { Name: "phone_number_verified", Value: "false" },
 *   { Name: "name", Value: "John Doe" },
 *   { Name: "custom:mfa_enabled", Value: "true" }
 * ]
 *
 * WHY THIS IS NEEDED:
 * User session (useSession) only returns basic auth status.
 * Full profile data needed for:
 * - Account settings display
 * - Profile editing forms
 * - Email/phone verification status
 * - MFA configuration state
 * - Display name and preferences
 *
 * SECURITY CONSIDERATIONS:
 * - Fetches only current user's data (session-based)
 * - No PII exposure risk (user's own data)
 * - Automatic refetch ensures fresh data after updates
 * - Disabled when not authenticated (prevents errors)
 *
 * @see Account.jsx - Account settings display
 * @see useUpdateUser - Updating user attributes
 * @see useSession - Basic authentication status
 * @see apiAuth.getUserDetails - Backend Cognito integration
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getUserData as getUserDetailsApi } from "../../services/apiAuth";
import type { UserResponse } from "../../types/api";

/**
 * Fetches and caches user profile data from Cognito.
 *
 * WHY CONDITIONAL ENABLING:
 * The enabled option prevents query execution when user is
 * not authenticated. This avoids unnecessary API calls and
 * error handling for unauthenticated states.
 *
 * WHY 5-MINUTE STALE TIME:
 * Profile data doesn't change frequently. 5 minutes provides
 * a good balance between data freshness and API call reduction.
 * User-initiated updates (useUpdateUser) invalidate this query
 * to show changes immediately.
 *
 * REFETCH STRATEGY:
 * - On mount: Only if data is stale (> 5 minutes)
 * - On window focus: Always (user may have updated elsewhere)
 * - On network reconnect: Always (ensure data consistency)
 * - After mutations: Manual invalidation by update hooks
 *
 * ERROR HANDLING:
 * - Unauthenticated: Query disabled, no errors
 * - Network error: Retry 3 times with exponential backoff
 * - Session expired: Caught by SessionContext, redirects to login
 *
 * @param {boolean} enabled - Whether to enable the query (default: true)
 * @returns {Object} Query result with user details and loading state
 */
export interface UseUserDetailsResult {
  user: UserResponse | null;
  isLoading: boolean;
  error: unknown;
  refetch: UseQueryResult<UserResponse | null>["refetch"];
  isFetching: boolean;
}

export function useUserDetails(enabled = true): UseUserDetailsResult {
  const accessToken =
    typeof window !== "undefined" ? localStorage.getItem("accessToken") : null;

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<UserResponse | null>({
    queryKey: ["user"],
    queryFn: getUserDetailsApi,
    enabled: enabled && !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });

  return {
    user: data ?? null,
    isLoading,
    error: error ?? null,
    refetch,
    isFetching,
  };
}
